import React, { useEffect, useMemo, useState } from "react";
import { CompetitionGroup } from "types/competition-analysis";
import { fetchCreatorsOverlappingData } from "services/api/competition-analysis";
import { useSelector } from "react-redux";
import { RootState } from "store";
import VennChart, { TooltipContext } from "Components/charts/VennChart";
import { formatNumber, stringToColor } from "utils/helpers";
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import { Loader } from "@chatscope/chat-ui-kit-react";

/* ---------------- TYPES ---------------- */

interface OverlapNode {
    id: string;
    name: string;
    value: number;
    color: string;
}

interface OverlapData {
    social_account: {
        uuid: string,
    },
    total: 100,
    overlaps: [
        {
            social_account_uuid: string,
            overlap_percent: number,
            overlap_count: number
        }
    ],
    unique_percent: number,
    unique_count: number
}

/* ---------------- COMPONENT ---------------- */

const CreatorOverlap: React.FC<{ competitionGroup: CompetitionGroup }> = ({
    competitionGroup
}) => {
    const selectedAgencyId = useSelector(
        (state: RootState) => state.auth.selectedAgencyId
    );

    const [selectedSocialAccountId, setSelectedSocialAccountId] = useState(competitionGroup.accounts[0]?.social_account?.uuid ?? '');

    const primary = useMemo(
        () =>
            competitionGroup.accounts.find(
                a => a.social_account.uuid === selectedSocialAccountId
            ),
        [competitionGroup.accounts, selectedSocialAccountId]
    );

    const [loading, setLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [overlapData, setOverlapData] = useState<Record<string, OverlapData>>({})

    /* ---------------- PRIMARY NODE ---------------- */

    const primaryNode = useMemo((): OverlapNode | undefined => {
        if (!primary) return undefined;
        return {
            id: primary.social_account.uuid,
            name: primary.social_account.name,
            value: 100,
            color: stringToColor(primary.social_account.handle)
        };
    }, [primary]);

    /* ---------------- ACCOUNT MAP ---------------- */

    const accountMap = useMemo(() => {
        const map = new Map<string, any>();
        competitionGroup.accounts.forEach(acc => {
            map.set(acc.social_account.uuid, {
                name: acc.social_account.name,
                handle: `@${acc.social_account.handle}`,
                avatar: acc.social_account.profile_picture_url,
                total: acc.total_insight?.creators ?? 0
            });
        });
        return map;
    }, [competitionGroup]);

    /* ---------------- DATA FETCH ---------------- */

    useEffect(() => {
        if (!selectedAgencyId || !selectedSocialAccountId) return;

        setLoading(true);

        fetchCreatorsOverlappingData(
            selectedAgencyId,
            competitionGroup.uuid,
            selectedSocialAccountId
        )
            .then(({ items }: { items: Array<any> }) => {

                const overlappingData = items.reduce<Record<string, OverlapData>>((acc, item) => {
                    acc[item.social_account.uuid] = item;
                    return acc;
                }, {});

                setOverlapData(overlappingData);
            })
            .finally(() => setLoading(false));
    }, [selectedAgencyId, selectedSocialAccountId, competitionGroup.uuid]);

    const nodes = useMemo(() => {
        if (!competitionGroup || !selectedSocialAccountId || Object.keys(overlapData).length === 0) return [];
        const competitionAccounts = competitionGroup.accounts;
        const overlaps = overlapData[selectedSocialAccountId]?.overlaps ?? [];

        const mapped: OverlapNode[] = overlaps.map((o: any) => {
            const acc = competitionAccounts.find(
                a => a.social_account.uuid === o.social_account_uuid
            );

            return {
                id: o.social_account_uuid,
                name: acc?.social_account.name ?? "Unknown",
                value: Number(o.overlap_percent.toFixed(1)),
                color: stringToColor(acc?.social_account?.handle ?? "")
            };
        });

        return mapped;
    }, [competitionGroup, overlapData, selectedSocialAccountId])

    const total = useMemo(() => {
        return overlapData?.[selectedSocialAccountId!]?.total ?? 0;
    }, [overlapData, selectedSocialAccountId])

    const uniquePercent = useMemo(() => {
        return overlapData?.[selectedSocialAccountId!]?.unique_percent ?? 0;
    }, [overlapData, selectedSocialAccountId])

    const uniqueCount = useMemo(() => {
        return overlapData?.[selectedSocialAccountId!]?.unique_count ?? 0;
    }, [overlapData, selectedSocialAccountId])

    const overlapCountMap = useMemo(() => {
        let overlapcountmap: any = {};
        for (let [key, overlapItem] of Object.entries(overlapData ?? {})) {
            overlapcountmap[key] = {};
            let overlaps = overlapItem.overlaps ?? [];
            for (let overlap of overlaps) {
                overlapcountmap[key][overlap.social_account_uuid] = overlap.overlap_count;
            }
        }

        return overlapcountmap;
    }, [overlapData])

    /* ---------------- TOOLTIP ---------------- */



    const renderVennTooltip = (ctx: TooltipContext) => {
        if (ctx.type === "intersection") {
            const primaryAcc = accountMap.get(ctx.primary.id);
            const nodeAcc = accountMap.get(ctx.node.id);

            const overlapCount = overlapCountMap?.[ctx.primary.id]?.[ctx.node.id]

            return (
                <div
                    style={{
                        display: "flex",
                        background: "#ffffff",
                        borderRadius: 12,
                        padding: "12px 14px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                        minWidth: 260,
                        gap: 12,
                        alignItems: "center"
                    }}
                >
                    {/* Avatars */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        {primaryAcc?.avatar && (
                            <img
                                src={primaryAcc.avatar}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    zIndex: 2
                                }}
                            />
                        )}

                        {nodeAcc?.avatar && (
                            <img
                                src={nodeAcc.avatar}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    marginLeft: -10,
                                    border: "2px solid #fff",
                                    zIndex: 1
                                }}
                            />
                        )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontWeight: 600,
                                color: "#111827"
                            }}
                        >
                            {primaryAcc?.name} ∩ {nodeAcc?.name}
                        </div>

                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 14,
                                fontWeight: 600
                            }}
                        >
                            {formatNumber(overlapCount)} creators
                        </div>
                    </div>
                </div>
            );
        }

        if (ctx.type === "node") {
            const acc = accountMap.get(ctx.node.id);

            return (
                <div
                    style={{
                        display: "flex",
                        background: "#ffffff",
                        borderRadius: 12,
                        padding: "12px 14px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                        minWidth: 220,
                        gap: 10,
                        alignItems: "center"
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontWeight: 600,
                                color: "#111827"
                            }}
                        >
                            {acc?.name}
                        </div>

                        <div
                            style={{
                                fontSize: 12,
                                color: "#6b7280"
                            }}
                        >
                            {acc?.handle}
                        </div>

                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 14,
                                fontWeight: 600
                            }}
                        >
                            {formatNumber(acc.total)} Creators
                        </div>
                    </div>

                    {acc?.avatar && (
                        <img
                            src={acc.avatar}
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                objectFit: "cover"
                            }}
                        />
                    )}
                </div>
            );
        }

        // primary
        const primaryAcc = accountMap.get(ctx.primary.id);

        return (
            <div
                style={{
                    display: "flex",
                    background: "#ffffff",
                    borderRadius: 12,
                    padding: "12px 14px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                    minWidth: 220,
                    gap: 10,
                    alignItems: "center"
                }}
            >
                <div style={{ flex: 1 }}>
                    <div
                        style={{
                            fontWeight: 600,
                            color: "#111827"
                        }}
                    >
                        {primaryAcc?.name}
                    </div>

                    <div
                        style={{
                            fontSize: 12,
                            color: "#6b7280"
                        }}
                    >
                        {primaryAcc?.handle}
                    </div>

                    <div
                        style={{
                            marginTop: 6,
                            fontSize: 14,
                            fontWeight: 600
                        }}
                    >
                        {formatNumber(primaryAcc.total)} Creators
                    </div>
                </div>

                {primaryAcc?.avatar && (
                    <img
                        src={primaryAcc.avatar}
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            objectFit: "cover"
                        }}
                    />
                )}
            </div>
        );
    };


    /* ---------------- RENDER ---------------- */

    return (
        <div className="card shadow m-3">
            <div className="card-header bg-green-light p-4">
                <h5 className="mb-0 heading dashboard-card-heading d-flex align-items-center gap-2">
                    <span className="dashboard-card-icon">
                        <i className="bx bx-intersect mt-1"></i>
                    </span>
                    Creator Overlap
                </h5>

            </div>

            <div className="row p-4 py-5">
                {/* LEFT: VENN */}
                <div className="col-md-8 d-flex justify-content-center">
                    {loading || !primaryNode ? (
                        <div className="d-flex align-items-center justify-content-center h-100 w-100">
                            <Loader />
                        </div>
                    ) : (
                        <VennChart
                            primaryNode={primaryNode}
                            nodes={nodes}
                            tooltipRenderer={renderVennTooltip}
                        />
                    )}
                </div>

                {/* RIGHT: SUMMARY CARD */}
                <div className="col-md-4 px-4">
                    <div
                        className="d-flex flex-column gap-3"
                        style={{
                            background: "#f7f5fb",
                            borderRadius: 16,
                            padding: 20,
                            minHeight: '360px',
                        }}
                    >
                        {/* DROPDOWN */}
                        <Dropdown
                            isOpen={dropdownOpen}
                            toggle={() => setDropdownOpen(!dropdownOpen)}
                        >
                            <DropdownToggle
                                caret
                                style={{
                                    width: "100%",
                                    background: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 12,
                                    color: "#374151",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "10px 12px"
                                }}
                            >
                                {/* LEFT: Avatar + Name */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <img
                                        src={primary?.social_account.profile_picture_url}
                                        alt=""
                                        style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            objectFit: "cover"
                                        }}
                                    />
                                    <span style={{ fontWeight: 500 }}>
                                        {primary?.social_account.name}
                                    </span>
                                </div>
                                {/* caret is automatically rendered on RIGHT */}
                            </DropdownToggle>

                            <DropdownMenu style={{ width: "100%", borderRadius: 12 }}>
                                {competitionGroup.accounts.map(acc => (
                                    <DropdownItem
                                        key={acc.social_account.uuid}
                                        onClick={() =>
                                            setSelectedSocialAccountId(acc.social_account.uuid)
                                        }
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8
                                        }}
                                    >
                                        <img
                                            src={acc.social_account.profile_picture_url}
                                            style={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: "50%"
                                            }}
                                        />
                                        {acc.social_account.name}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>


                        {/* TOTAL */}
                        <div className="mt-4">
                            <div className="text-uppercase text-muted small">
                                Total Creators
                            </div>
                            <strong>{formatNumber(total)}</strong>
                        </div>

                        {/* OVERLAP */}
                        <div className="mt-4">
                            <div className="text-uppercase text-muted small">
                                Creator Overlap
                            </div>

                            {nodes.map(n => {
                                const acc = accountMap.get(n.id);
                                return (
                                    <div
                                        key={n.id}
                                        className="d-flex align-items-center justify-content-between mt-2"
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <img
                                                src={acc?.avatar}
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%"
                                                }}
                                            />
                                            <span>{acc?.name}</span>
                                        </div>
                                        <span>{formatNumber(overlapCountMap[selectedSocialAccountId][n.id] ?? 0)}</span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* UNIQUE */}
                        <div className="mt-4">
                            <div className="text-uppercase text-muted small">
                                Unique Creators
                            </div>
                            <strong>{formatNumber(uniqueCount)}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreatorOverlap;
